import { useState, useRef, useEffect } from "react";
import { IconButton, LinearProgress } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import "./VoiceAudioRecorder.css";
import { FileUpload, RadioButtonChecked, Upload } from "@mui/icons-material";
import api from '../../api/api';
import { useAuth } from '../../features/auth/AuthContext';
import audiorecorder from '../../assets/images/audiorecorder.svg';
import deleteIcon from '../../assets/images/delete.svg';
import UploadIcon from '../../assets/images/upload.svg';
import { Tooltip } from 'react-tooltip';
import Play from "../../assets/images/play_arrow.svg"
import Pause from "../../assets/images/pause.svg"
import stop from "../../assets/images/stop.svg"

const VoiceAudioRecorder = ({
  onAudioSave,
  disabled = false,
  onAudioUploaded,
  assignment_id,
  student_id,
  question_id,
  question_type,
  assignment_type,
  student_answer_json,
  practice_assignment_id,
  onRecordingStateChange,
  onAudioStateChange,
  existingAudio = null,
  setIsTimerPaused,
  onFileOperationChange,
  isFileOperationInProgress,
  isTeacher,
  setIsVideoBlocked
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // Progress state to track the recording time
  const [playbackProgress, setPlaybackProgress] = useState(0); // Playback progress (0-100)
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0); // Current playback time in seconds
  const [audioDuration, setAudioDuration] = useState(0); // Total audio duration
  const [uploadedFile, setUploadedFile] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const isCancelingRef = useRef(false);

  const { token } = useAuth();

  //Load existing audio when component mounts or existingAudio changes
  useEffect(() => {
    if (existingAudio && existingAudio.serverUrl) {
      console.log('Loading existing audio:', existingAudio);
      setUploadedFile(existingAudio);
      setHasRecording(false); // Not a local recording
      setAudioBlob(null);
      notifyAudioState(false, true); // Notify that audio exists
    } else {
      setUploadedFile(null);
    }
  }, [existingAudio]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const notifyAudioState = (recording, hasAudio) => {
    if (onRecordingStateChange) {
      onRecordingStateChange(recording);
    }
    if (onAudioStateChange) {
      // Audio is "active" if recording OR has a recording ready
      onAudioStateChange(recording || hasAudio);
    }
  };


  const startRecording = async () => {
    if (disabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0 && !isCancelingRef.current) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Check if there are chunks (not canceled)
        if (audioChunksRef.current.length > 0 && !isCancelingRef.current) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          setAudioBlob(audioBlob);
          setHasRecording(true);
          notifyAudioState(false, true);

        }
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      if (onRecordingStateChange) {
        onRecordingStateChange(true);
      }
      setRecordingTime(0);
      setProgress(0);

      notifyAudioState(true, false);
      setIsVideoBlocked(true)


      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
        setProgress((prev) => Math.min((prev + 1) / 100, 1));
      }, 1000);

    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCancelingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setIsVideoBlocked(false)

      if (onRecordingStateChange) {
        onRecordingStateChange(false);
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  };
  const cancelRecording = () => {
    // ✅ Set cancel flag FIRST - this prevents any new chunks from being added
    isCancelingRef.current = true;

    // Clear chunks
    audioChunksRef.current = [];

    if (mediaRecorderRef.current) {
      // Stop the recorder
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // Stop all media stream tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }

      mediaRecorderRef.current = null;
    }

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset all states
    setIsRecording(false);
    setIsVideoBlocked(false)
    setIsPaused(false);
    setRecordingTime(0);
    setProgress(0);
    setHasRecording(false);
    setAudioBlob(null);

    if (onRecordingStateChange) {
      onRecordingStateChange(false);
    }

    notifyAudioState(false, false);
    toast.success('Recording deleted successfully!');

  };

  const deleteRecording = () => {
    // Clear audio chunks
    audioChunksRef.current = [];

    setHasRecording(false);
    setAudioBlob(null);
    setRecordingTime(0);
    setIsPlaying(false);
    setProgress(0);
    setPlaybackProgress(0);
    setCurrentPlaybackTime(0);
    setAudioDuration(0);

    if (audioRef.current) {
      // Remove all event listeners before cleanup
      audioRef.current.onloadedmetadata = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current.onended = null;

      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    notifyAudioState(false, false);
    toast.success('Recording deleted successfully!');
  };
  const handleProgressClick = (event) => {
    if (!audioRef.current || !audioBlob) return;

    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * audioRef.current.duration;

    audioRef.current.currentTime = newTime;
    setCurrentPlaybackTime(newTime);
    setPlaybackProgress(percentage * 100);
  };

  // const saveRecording = async () => {
  //   if (!audioBlob) return;

  //   try {
  //     // Convert webm to mp3-like format (we'll keep as webm but name it appropriately)
  //     const timestamp = new Date().getTime();
  //     const fileName = `Audio-${Math.floor(recordingTime / 60)}m-${recordingTime % 60}s-${timestamp}.webm`;

  //     const file = new File([audioBlob], fileName, {
  //       type: 'audio/webm',
  //       lastModified: timestamp
  //     });

  //     // Call the parent callback with the audio file
  //     if (onAudioSave) {
  //       onAudioSave(file);
  //       toast.success("Audio recording saved successfully!");
  //     }

  //     // Reset state including playback states
  //     deleteRecording();
  //   } catch (error) {
  //     console.error("Error saving audio:", error);
  //     toast.error("Failed to save audio recording");
  //   }
  // };


  // const saveRecording = async () => {
  //   if (!audioBlob) return;

  //   try {
  //     // Build a file from the blob (same as you already do)
  //     const ts = Date.now();
  //     const fileName = `Audio-${Math.floor(recordingTime / 60)}m-${recordingTime % 60}s-${ts}.webm`;
  //     const file = new File([audioBlob], fileName, {
  //       type: 'audio/webm',
  //       lastModified: ts,
  //     });

  //     // (Optional) keep legacy behavior for parents expecting the raw File
  //     if (onAudioSave) {
  //       onAudioSave(file);
  //     }

  //     // === Build FormData exactly like handleUploadFiles ===
  //     const formData = new FormData();
  //     formData.append('student_id', String(student_id));
  //     formData.append('question_id', String(question_id));
  //     formData.append('question_type', String(question_type));
  //     formData.append('student_answers_json', JSON.stringify(student_answer_json || {}));

  //     if (typeof practice_assignment_id !== 'undefined' && practice_assignment_id !== null) {
  //       formData.append('practice_assignment_id', String(practice_assignment_id));
  //     } else {
  //       formData.append('assignment_id', String(assignment_id));
  //     }

  //     // Single audio file (append once; preserves order for 1 file)
  //     formData.append('files', file);

  //     // === Call API (same endpoint, headers, timeout) ===
  //     const response = await api.post(
  //       'submissions/upload-answer/',
  //       formData,
  //       {
  //         headers: {
  //           Authorization: `Token ${token}`,
  //           'Content-Type': 'multipart/form-data',
  //         },
  //         timeout: 600000, // 10 minutes
  //         // onUploadProgress: (e) => console.log('upload', e.loaded, e.total)
  //       }
  //     );

  //     if (response.status === 200 || response.status === 201) {
  //       // Parse server response: file_name1, file_url1, ...
  //       const serverName = response?.data?.file_name1 || file.name;
  //       const serverUrl = response?.data?.file_url1;

  //       if (!serverUrl) {
  //         // Server didn’t return url for some reason
  //         toast.warning('Uploaded, but server did not return a file URL.');
  //       }

  //       // If you want to notify parent with the uploaded record including URL:
  //       // if (typeof onUploadComplete === 'function') {
  //       //   onUploadComplete({
  //       //     id: crypto?.randomUUID?.() ?? String(ts),
  //       //     name: serverName,
  //       //     size: file.size,
  //       //     type: file.type,
  //       //     serverUrl,
  //       //     // keep preview URL if you need it:
  //       //     url: (typeof window !== 'undefined' && URL?.createObjectURL) ? URL.createObjectURL(file) : undefined,
  //       //     isLoading: false,
  //       //     isNew: false,
  //       //   });
  //       // }

  //       toast.success('Audio recording uploaded successfully!');
  //       // Reset local recorder state
  //       deleteRecording();
  //       return { serverUrl, name: serverName };
  //     }

  //     // Non-2xx status
  //     throw new Error('Upload failed');
  //   } catch (error) {
  //     console.error('Error uploading audio:', error);
  //     const errorMessage =
  //       error?.response?.data?.message ||
  //       error?.response?.data?.error ||
  //       'Failed to upload audio recording. Please try again.';
  //     toast.error(errorMessage);
  //   }
  // };

  const saveRecording = async () => {
    if (!audioBlob) return;

    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setPlaybackProgress(0);
    setCurrentPlaybackTime(0);

    setIsTimerPaused(false);
    // alert('Saving recording...');
    onFileOperationChange(true);

    try {
      // Build a file from the blob (same as you already do)
      const ts = Date.now();
      const fileName = `Audio-${Math.floor(recordingTime / 60)}m-${recordingTime % 60}s-${ts}.webm`;
      const file = new File([audioBlob], fileName, {
        type: 'audio/webm',
        lastModified: ts,
      });

      // Generate unique ID
      const fileId = Math.random().toString(36).substr(2, 9);

      // ✅ Add to queue first with loading state
      if (onAudioSave) {
        onAudioSave({
          id: fileId,
          file,
          name: fileName,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
          isNew: true,
          isLoading: true
        });
      }
      const formData = new FormData();
      formData.append('student_id', String(student_id));
      formData.append('question_id', String(question_id));
      formData.append('question_type', String(question_type));
      formData.append('student_answers_json', JSON.stringify(student_answer_json || {}));
      formData.append('file_type', String('audio'));

      if (typeof practice_assignment_id !== 'undefined' && practice_assignment_id !== null) {
        formData.append('practice_assignment_id', String(practice_assignment_id));
      } else {
        formData.append('assignment_id', String(assignment_id));
      }

      // Single audio file (append once; preserves order for 1 file)
      formData.append('files', file);

      // === Call API (same endpoint, headers, timeout) ===
      const response = await api.post(
        'submissions/upload-answer/',
        formData,
        {
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 600000, // 10 minutes
          // onUploadProgress: (e) => console.log('upload', e.loaded, e.total)
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Parse server response: file_name1, file_url1, ...
        const serverName = response?.data?.file_name1 || fileName;
        const serverUrl = response?.data?.file_url1;

        if (!serverUrl) {
          // Server didn’t return url for some reason
          toast.warning('Uploaded, but server did not return a file URL.');
        }

        //   if (onAudioUploaded) {
        //     onAudioUploaded({
        //       id: fileId,
        //       name: serverName,
        //      size: file.size,
        //       type: file.type,
        //        url: URL.createObjectURL(file),
        //      serverUrl: serverUrl,
        //      isLoading: false,
        //      isNew: false
        // });
        // }

        const uploadedData = {
          id: fileId,
          name: serverName,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
          serverUrl: serverUrl,
          isLoading: false,
          isNew: false
        };

        // ✅ MODIFIED: Set uploaded file state instead of deleting
        // setUploadedFile(uploadedData);
        setHasRecording(false); // Clear local recording state
        setAudioBlob(null);
        setRecordingTime(0);
        setProgress(0);
        setPlaybackProgress(0);
        setCurrentPlaybackTime(0);
        setAudioDuration(0);
        setIsPlaying(false);
        setIsTimerPaused(true);

        onFileOperationChange(false);
        // Clean up local audio element
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
          audioRef.current = null;
        }

        if (onAudioUploaded) {
          onAudioUploaded(uploadedData);
        }

        // Reset audio state so mic button gets enabled
        notifyAudioState(false, false);

        toast.success('Audio recording uploaded successfully!');
        // Reset local recorder state
        // deleteRecording();
        return { serverUrl, name: serverName };
      }

      // Non-2xx status
      throw new Error('Upload failed');
    } catch (error) {
      console.error('Error uploading audio:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to upload audio recording. Please try again.';
      toast.error(errorMessage);
      // ✅ Notify parent about failure
      if (onAudioUploaded) {
        onAudioUploaded(null);
      }
    } finally {
      onFileOperationChange?.(false);
      setIsTimerPaused?.(true);
    }
  };


  const togglePlayPause = () => {
    if (!audioBlob) {
      toast.error("No recording available to play.");
      return;
    }

    // Create an audio element only once
    if (!audioRef.current) {
      audioRef.current = new Audio(URL.createObjectURL(audioBlob));

      // Set up event listeners with null checks
      audioRef.current.onloadedmetadata = () => {
        if (audioRef.current) {
          setAudioDuration(audioRef.current.duration);
        }
      };

      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          const current = audioRef.current.currentTime;
          const duration = audioRef.current.duration;
          setCurrentPlaybackTime(current);
          setPlaybackProgress((current / duration) * 100);
        }
      };

      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlaybackProgress(0);
        setCurrentPlaybackTime(0);
      };
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          console.error('Error playing audio:', error);
          toast.error('Failed to play the audio.');
        });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (hasRecording && !isFileOperationInProgress) {
    return (
      <div className="voice-audio-recorder-container saved-state">
        <div className="audio-preview">
          <IconButton
            onClick={togglePlayPause}
            className="play-pause-button"
            sx={{
              backgroundColor: "#0068ff",
              color: "white",
              "&:hover": {
                backgroundColor: "#0056d2",
              },
              width: 40,
              height: 40,
            }}
          >
            {isPlaying ? <img src={Pause} /> : <img src={Play} />}
          </IconButton>

          <div className="audio-info">
            <div className="waveform-preview" onClick={handleProgressClick} style={{ cursor: 'pointer' }}>
              <LinearProgress
                variant="determinate"
                value={playbackProgress}
                sx={{
                  width: "100%",
                  height: "10px",
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#0068ff",
                  },
                }}
              />
            </div>
            <span className="recording-time">
              {/* {formatTime(Math.floor(currentPlaybackTime))} / */}
              {formatTime(recordingTime)}
            </span>
          </div>

          <div className="action-buttons">
            <IconButton
              onClick={saveRecording}
              className="save-button"
              sx={{
                color: "rgb(0, 104, 255)",
              }}
            >
              <img src={UploadIcon} alt="Upload" />
            </IconButton>
            <IconButton
              onClick={deleteRecording}
              className="delete-button"
              sx={{
                color: "#000000",
              }}
            >
              <img src={deleteIcon} alt="Delete" />
            </IconButton>
          </div>
        </div>
      </div>
    );
  }

  if (isRecording && !isFileOperationInProgress) {
    return (
      <div className="voice-audio-recorder-container recording-state">
        <div className="recording-display">
          <div className="waveform-container-audio">
            <div className="waveform-audio">
              <div className="boxContainer">
                <div className="box box1"></div>
                <div className="box box2"></div>
                <div className="box box4"></div>
                <div className="box box3"></div>
                <div className="box box5"></div>
              </div>
              <p>Recording...</p>
            </div>
          </div>

          <span className="recording-time">{formatTime(recordingTime)}</span>

          <div className="recording-controls">

            <IconButton
              onClick={stopRecording}
              className="stop-button"
              sx={{
                backgroundColor: "#ff4444",
                color: "white",
                "&:hover": {
                  backgroundColor: "#cc0000",
                },
                width: 48,
                height: 48,
              }}
            >
              <img src={stop} />
            </IconButton>
            <IconButton
              onClick={cancelRecording}
              className="delete-button"
              sx={{
                color: "#00000",
              }}
            >
              <img src={deleteIcon} alt="Delete" />
            </IconButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="voice-audio-recorder-container">
      <IconButton
        onClick={startRecording}
        data-tooltip-id="record-audio-tooltip"
        disabled={disabled || isTeacher}
        className="audio-mic-button"
        sx={{
          backgroundColor: "#ffffff",
          color: "#0068ff",
          "&:hover": {
            backgroundColor: "#e0ebff",
          },
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed !important" : "pointer",
          color: disabled ? "grey" : "#0068FF"
        }}
      >
        <img src={audiorecorder} alt="Audio Recorder" style={{ filter: disabled || isTeacher ? 'grayscale(100%)' : 'none', cursor: disabled ? 'not-allowed !important' : 'pointer', width: "0.7em" }} />
      </IconButton>
      <Tooltip
        id="record-audio-tooltip"
        place="top"
        content={"Record Answer"}
        type="light"
        effect="float"
      />
    </div>
  );
};

export default VoiceAudioRecorder;